/**
 * Main entry point for Regalia game interface
 * This file will be bundled by esbuild into dist/regalia.js
 * 
 * IMPORTANT: Setup functions must be available globally because Game.js calls them.
 * Since Game.js loads before this bundle, we need to ensure Setup functions
 * are exposed on the window object when this bundle loads.
 */

// Import all models to make Setup functions available
import {
    SetupActionData,
    SetupCharacterData,
    SetupCheckData,
    SetupCommandData,
    SetupConditionData,
    SetupCustomPropertyData,
    SetupEnhInputData,
    SetupExitData,
    SetupObjectData,
    SetupPlayerData,
    SetupImageData,
    SetupRoomData,
    SetupStatusBarData,
    SetupTimerData,
    SetupVariableData
} from './models';

// Make Setup functions globally available on window
// These are called by Game.js SetupGameData() function
declare global {
    interface Window {
        SetupActionData: typeof SetupActionData;
        SetupCharacterData: typeof SetupCharacterData;
        SetupCheckData: typeof SetupCheckData;
        SetupCommandData: typeof SetupCommandData;
        SetupConditionData: typeof SetupConditionData;
        SetupCustomPropertyData: typeof SetupCustomPropertyData;
        SetupEnhInputData: typeof SetupEnhInputData;
        SetupExitData: typeof SetupExitData;
        SetupObjectData: typeof SetupObjectData;
        SetupPlayerData: typeof SetupPlayerData;
        SetupImageData: typeof SetupImageData;
        SetupRoomData: typeof SetupRoomData;
        SetupStatusBarData: typeof SetupStatusBarData;
        SetupTimerData: typeof SetupTimerData;
        SetupVariableData: typeof SetupVariableData;
    }
}

window.SetupActionData = SetupActionData;
window.SetupCharacterData = SetupCharacterData;
window.SetupCheckData = SetupCheckData;
window.SetupCommandData = SetupCommandData;
window.SetupConditionData = SetupConditionData;
window.SetupCustomPropertyData = SetupCustomPropertyData;
window.SetupEnhInputData = SetupEnhInputData;
window.SetupExitData = SetupExitData;
window.SetupObjectData = SetupObjectData;
window.SetupPlayerData = SetupPlayerData;
window.SetupImageData = SetupImageData;
window.SetupRoomData = SetupRoomData;
window.SetupStatusBarData = SetupStatusBarData;
window.SetupTimerData = SetupTimerData;
window.SetupVariableData = SetupVariableData;

// Export core modules
export * from './core/Globals';
export * from './core/Settings';
export * from './core/GameController';
export * from './core/GameUI';
export * from './core/Initialization';

// Export utilities
export * from './utils/DOM';
export * from './utils/Constants';
export * from './utils/DateTimes';
export * from './utils/Finder';
export * from './utils/Utilities';
export * from './utils/TextHandler';
export * from './utils/Logging';
export * from './utils/Cheat';
export * from './utils/GameDump';

// Export game logic modules
export * from './game/CommandLists';
export * from './game/GameTimers';
export * from './game/GameActions';
export * from './game/GameConditions';
export * from './game/GameHistory';
export * from './game/SaveAndLoad';
export * from './game/GameCommands';

// Export React components
export * from './components/CheatMenuComponents';
export * from './components/CheatMenuTabs';
export * from './components/Dialog';
export * from './components/SaveLoadDialog';
export * from './components/OptionsDialog';
export * from './components/SaveLoadDialogContent';
export * from './components/ExportDialog';
export * from './components/ImportDialog';
export * from './components/OptionsDialogContent';
export * from './components/DownloadPackageDialog';
export * from './components/PaginatedTable';
export * from './components/VariableEditDialog';
export * from './components/PropertyEditDialog';
export * from './components/PropertiesDialog';
export * from './components/MoveObjectDialog';
export * from './components/MoveCharacterDialog';
export * from './components/PlayerEditDialog';
export * from './components/ConfirmDialog';
export * from './components/AlertDialog';
export * from './components/SaveNameDialog';
export * from './components/RenameSaveDialog';
export * from './utils/DialogHelpers';

// Make Finder and Utilities globally available
import { Finder } from './utils/Finder';
import * as Utils from './utils/Utilities';
import { Globals } from './core/Globals';
(window as any).Finder = Finder;
(window as any).Globals = Globals;
// Utilities functions are already exposed globally in Utilities.ts

// Expose model constructors globally for Game.js compatibility
// Game.js uses lowercase names (e.g., "new player()")
import { Player } from './models/Player';
import { Room } from './models/Room';
import { Character } from './models/Character';
import { RagsObject } from './models/Object';
import { RagsImage } from './models/RagsImage';
import { Variable } from './models/Variable';
import { Timer } from './models/Timer';
import { StatusBarItem } from './models/StatusBarItem';
import { Condition } from './models/Condition';
import { Command } from './models/Command';
import { Action } from './models/Action';

// Expose as both uppercase (TypeScript) and lowercase (Game.js compatibility)
(window as any).Player = Player;
(window as any).player = Player;  // Game.js uses lowercase
(window as any).Room = Room;
(window as any).room = Room;  // Game.js uses lowercase
(window as any).Character = Character;
(window as any).character = Character;  // Game.js uses lowercase
(window as any).RagsObject = RagsObject;
(window as any).object = RagsObject;  // Game.js uses lowercase
(window as any).RagsImage = RagsImage;
(window as any).image = RagsImage;  // Game.js uses lowercase
(window as any).Variable = Variable;
(window as any).variable = Variable;  // Game.js uses lowercase
(window as any).Timer = Timer;
(window as any).timer = Timer;  // Game.js uses lowercase
(window as any).StatusBarItem = StatusBarItem;
(window as any).statusbaritem = StatusBarItem;  // Game.js uses lowercase
(window as any).Condition = Condition;
(window as any).ragscondition = Condition;  // Game.js uses lowercase
(window as any).Command = Command;
(window as any).command = Command;  // Game.js uses lowercase
(window as any).Action = Action;
(window as any).action = Action;  // Game.js uses lowercase

// Initialize global game state variables
if (typeof (window as any).TheGame === 'undefined') {
    (window as any).TheGame = null;
}
if (typeof (window as any).OriginalGame === 'undefined') {
    (window as any).OriginalGame = null;
}

// Game logic modules are already exposed globally in their respective files
